import type { RouteObject } from 'react-router'

const lazy = (loader: () => Promise<{ default: React.ComponentType }>) => async () => {
  const m = await loader()
  return { Component: m.default }
}
const named =
  <K extends string>(loader: () => Promise<Record<K, React.ComponentType>>, key: K) =>
  async () => {
    const m = await loader()
    return { Component: m[key] }
  }

export const adminRoutes: RouteObject[] = [
  { index: true, lazy: lazy(() => import('./Dashboard')) },
  { path: 'users', lazy: lazy(() => import('./Users')) },
  { path: 'crm/people', lazy: lazy(() => import('./crm/People')) },
  { path: 'crm/people/:id', lazy: lazy(() => import('./crm/PersonDetail')) },
  { path: 'crm/organizations', lazy: lazy(() => import('./crm/Organizations')) },
  { path: 'crm/organizations/:id', lazy: lazy(() => import('./crm/OrganizationDetail')) },
  { path: 'crm/tags', lazy: lazy(() => import('./crm/Tags')) },
  { path: 'content/programs', lazy: lazy(() => import('./content/Programs')) },
  { path: 'content/events', lazy: named(() => import('./content/Events'), 'AdminEvents') },
  { path: 'content/events/:id', lazy: named(() => import('./content/Events'), 'AdminEventDetail') },
  {
    path: 'content/resources',
    lazy: named(() => import('./content/ResourcesBlocks'), 'AdminResources'),
  },
  { path: 'content/blocks', lazy: named(() => import('./content/ResourcesBlocks'), 'AdminBlocks') },
  {
    path: 'applications',
    lazy: named(() => import('./applications/Applications'), 'AdminApplications'),
  },
  {
    path: 'applications/:id',
    lazy: named(() => import('./applications/Applications'), 'AdminApplicationDetail'),
  },
  { path: 'forms', lazy: named(() => import('./applications/Forms'), 'AdminForms') },
  {
    path: 'forms/discount-codes',
    lazy: named(() => import('./applications/Forms'), 'DiscountCodes'),
  },
  { path: 'forms/:id', lazy: named(() => import('./applications/Forms'), 'FormBuilder') },
  { path: 'review', lazy: named(() => import('./applications/Review'), 'ReviewQueue') },
  { path: 'mutual-aid', lazy: named(() => import('./applications/MutualAid'), 'AdminMutualAid') },
  { path: 'review/:id', lazy: named(() => import('./applications/Review'), 'ReviewApplication') },
  { path: 'finance', lazy: named(() => import('./finance/Finance'), 'FinanceTransactions') },
  {
    path: 'finance/transactions/:id',
    lazy: named(() => import('./finance/Finance'), 'FinanceTransactionDetail'),
  },
  { path: 'maps', lazy: named(() => import('./maps/Maps'), 'AdminMaps') },
  { path: 'maps/:id', lazy: named(() => import('./maps/MapEditor'), 'MapEditor') },
  {
    path: 'scholarships',
    lazy: named(() => import('./scholarships/Scholarships'), 'AdminScholarships'),
  },
  {
    path: 'scholarships/:id',
    lazy: named(() => import('./scholarships/Scholarships'), 'AdminScholarshipDetail'),
  },
  {
    path: 'fundraising',
    lazy: named(() => import('./fundraising/Fundraising'), 'AdminFundraising'),
  },
  {
    path: 'fundraising/campaigns/:id',
    lazy: named(() => import('./fundraising/Fundraising'), 'AdminCampaignDetail'),
  },
  {
    path: 'fundraising/donations',
    lazy: named(() => import('./fundraising/Fundraising'), 'AdminDonations'),
  },
  {
    path: 'fundraising/recurring',
    lazy: named(() => import('./fundraising/Fundraising'), 'AdminRecurring'),
  },
  {
    path: 'fundraising/statements',
    lazy: named(() => import('./fundraising/Statements'), 'AdminStatements'),
  },
  { path: 'sponsors', lazy: named(() => import('./sponsors/Sponsors'), 'AdminSponsors') },
  { path: 'sponsors/:id', lazy: named(() => import('./sponsors/Sponsors'), 'AdminSponsorDetail') },
  { path: 'volunteers', lazy: named(() => import('./volunteers/Volunteers'), 'AdminVolunteers') },
  {
    path: 'volunteers/interests',
    lazy: named(() => import('./volunteers/Interests'), 'AdminVolunteerInterests'),
  },
  {
    path: 'volunteers/hours',
    lazy: named(() => import('./volunteers/Volunteers'), 'AdminVolunteerHours'),
  },
  {
    path: 'volunteers/:id',
    lazy: named(() => import('./volunteers/Volunteers'), 'AdminOpportunity'),
  },
  { path: 'shop', lazy: named(() => import('./shop/Shop'), 'AdminShop') },
  { path: 'shop/orders', lazy: named(() => import('./shop/Shop'), 'AdminOrders') },
  { path: 'shop/orders/:id', lazy: named(() => import('./shop/Shop'), 'AdminOrderDetail') },
  { path: 'accounting', lazy: lazy(() => import('./accounting/Accounting')) },
  { path: 'reports', lazy: lazy(() => import('./reports/Reports')) },
  { path: 'audit', lazy: named(() => import('./system/System'), 'Audit') },
  { path: 'settings', lazy: named(() => import('./system/System'), 'Settings') },
  { path: 'integrations', lazy: lazy(() => import('./system/Integrations')) },
  { path: 'branding', lazy: lazy(() => import('./system/Branding')) },
  { path: 'import/zeffy', lazy: lazy(() => import('./system/ZeffyImport')) },
  { path: 'communications', lazy: named(() => import('./system/System'), 'Communications') },
  { path: 'email-lists', lazy: lazy(() => import('./system/EmailLists')) },
  {
    path: 'newsletters',
    lazy: named(() => import('./newsletters/Newsletters'), 'AdminNewsletters'),
  },
  {
    path: 'newsletters/:id',
    lazy: named(() => import('./newsletters/Newsletters'), 'AdminNewsletter'),
  },
  { path: 'love', lazy: named(() => import('./love/Love'), 'AdminLove') },
  { path: 'love/:id', lazy: named(() => import('./love/Love'), 'AdminLoveWall') },
  { path: 'photos', lazy: named(() => import('./photos/Photos'), 'AdminPhotos') },
  { path: 'live', lazy: named(() => import('./festival/LiveAlerts'), 'AdminLiveAlerts') },
  { path: 'checkin', lazy: named(() => import('./festival/Checkin'), 'AdminCheckin') },
  {
    path: 'festival/applications',
    lazy: named(() => import('./festival/Participation'), 'AdminFestivalApplications'),
  },
  {
    path: 'festival/applications/:id',
    lazy: named(() => import('./festival/Participation'), 'AdminFestivalApplicationDetail'),
  },
  {
    path: 'festival/booths',
    lazy: named(() => import('./festival/BoothTypes'), 'AdminBoothTypes'),
  },
  {
    path: 'checkin/print',
    lazy: named(() => import('./festival/Checkin'), 'AdminCheckinSheet'),
  },
  { path: '*', lazy: lazy(() => import('../public/NotFound')) },
]
